<template>
	<div>
		<policy-submenu />

		<core-layout-content full>
			<core-layout-headline-and-actions>
				<template slot="headline">{{ $t("endorsements") }}</template>
				<template slot="actions">
					<j-button type="primary" @click="createEndorsement">
						<crm-icon name="add" size="small" color="c5" />
						{{ $t("create_endorsement") }}
					</j-button>
				</template>
			</core-layout-headline-and-actions>

			<!-- Data Container -->
			<j-card class="bg5">
				<j-data-table
					:header="header"
					:list="endorsements"
					:sortable="false"
					:isLoading="isLoading"
					@sort="onSort($event)"
				>
					<template slot="list" slot-scope="{ item }">
						<td v-for="(column, i) in header" :key="i">
							<!-- createdAt -->
							<div v-if="column.field === 'createdAt'">
								{{ item[column.field] }}
							</div>
							<!-- status -->
							<j-tag
								v-if="column.field === 'status'"
								:label="item[column.field]"
								:variant="decideStatusClass(item.status)"
							/>
							<!-- actions -->
							<template v-if="column.field === 'actions'">
								<!-- view/edit details -->
								<div class="flex xs-12 xs-right">
									<j-button
										type="tertiary"
										class="c2 cu2 fw7 mr4"
										@click="openEndorsementEditor(item.id)"
										>{{
											item.status === "CREATED" ? $t("edit") : $t("view")
										}}</j-button
									>
									<!-- pdf  -->
									<crm-icon
										name="file-download"
										color="c1"
										class="cu2"
										@click.native="handleDownload(item)"
									/>
								</div>
							</template>
						</td>
					</template>
					<template slot="empty">
						{{
							isEndorsable
								? $t("errors.no_data")
								: $t("errors.policy_not_issued")
						}}
					</template>
				</j-data-table>
			</j-card>

			<!-- Download pdf -->
			<core-modal
				name="downloadPdf"
				:headline="$t('download_pdf')"
				@close="fetchEndorsements"
			>
				<div class="p4">
					<!-- Select template -->
					<div class="fw7 pt2 pb2">{{ $t("choose_pdf_template") }}</div>
					<j-base-multi-select-with-flexible-value
						v-model="selectedTemplate"
						:options="templates"
						:allowMultiselect="false"
						:allowLocalSearch="true"
						placeholder="Search template..."
						class="mb15"
					/>
					<!-- <core-graphql-variables-builder
						:graphQlQuery="graphQlQuery"
						:values="getValues()"
						@update:variables="graphQlVariables = $event"
						class="mb3"
					/>-->
					<j-button
						class="mt15"
						size="small"
						@click="downloadPreviewFile"
						:isLoading="isLoading"
						>{{ $t("download_pdf") }}</j-button
					>
				</div>
			</core-modal>
		</core-layout-content>
	</div>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import PolicySubmenu from "../components/PolicySubmenu"
import CoreModal from "@/modules/core/components/CoreModal"
import dayjs from "dayjs"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import get from "lodash.get"
import JTag from "@/components/JTag/"
// import CoreGraphqlVariablesBuilder from "@/modules/core/components/CoreGraphqlVariablesBuilder"
import JBaseMultiSelectWithFlexibleValue from "@/components/JBaseMultiSelectWithFlexibleValue"

export default {
	components: {
		CrmIcon,
		JButton,
		JCard,
		JDataTable,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		PolicySubmenu,
		CoreModal,
		JTag,
		// CoreGraphqlVariablesBuilder,
		JBaseMultiSelectWithFlexibleValue
	},

	props: {
		// From router - policyId
		id: String
	},

	data: () => ({
		isLoading: false,
		endorsements: [],
		status: "",
		templates: null,
		selectedTemplate: null,
		selectedEndorsement: null
	}),

	computed: {
		isEndorsable() {
			return this.status === "ISSUED"
		},
		openEndorsement() {
			return (
				(this.endorsements &&
					this.endorsements.length &&
					this.endorsements.filter(({ status }) => status === "CREATED")
						.length &&
					this.endorsements.filter(({ status }) => status === "CREATED")[
						this.endorsements.filter(({ status }) => status === "CREATED")
							.length - 1
					]) ||
				null
			)
		},
		header() {
			return [
				{ text: this.$t("created"), field: "createdAt" },
				{ text: this.$t("status"), field: "status" },
				{ text: this.$t("actions"), field: "actions" }
			]
		},
		graphQlQuery() {
			return this.selectedTemplate?.[0]?.graphQlQuery
		}
	},

	methods: {
		decideStatusClass(status) {
			if (status === "APPROVED") return "success"
			if (status === "REJECTED") return "error"
			else return "neutral"
		},
		openEndorsementEditor(endorsementId) {
			if (!endorsementId) return
			this.$router.push({
				name: "EndorsementsUpdate",
				params: { id: this.id, endorsementId: endorsementId }
			})
		},
		handleDownload(item) {
			this.selectedEndorsement = item
			this.$modal.show("downloadPdf")
		},
		async fetchEndorsements() {
			// TODO: fetch endorsements, if error show error
			const query = gql`
				query policies($where: policyWhereInput) {
					policies(where: $where) {
						list {
							id
							status
							endorsements {
								id
								createdAt
								lastModifiedAt
								status
							}
						}
					}
				}
			`

			const {
				data: {
					policies: {
						list: [{ id, status, endorsements }]
					}
				},
				errors
			} = await api({
				query,
				variables: { where: { id: this.id } }
			})
			if (errors && errors.length) {
				this.$store.dispatch("showError", errors?.[0].message)
				return
			}

			// Format createdAt
			this.endorsements = endorsements.map(item => {
				return {
					...item,
					createdAt: item.createdAt
						? dayjs(item.createdAt).format(
								this.$store.state.settings.dateTimeFormat
						  )
						: null
				}
			})
			this.status = status
		},

		async getCaseIdProposalId() {
			const query = gql`
				query cases(
					$caseWhere: caseWhere
					$policyWhere1: policyWhereInput
					$proposalWhere: proposalWhere
				) {
					cases(where: $caseWhere) {
						list {
							id
							proposals(where: $proposalWhere) {
								id
								policies(where: $policyWhere1) {
									id
								}
							}
						}
					}
				}
			`
			const variables = {
				policyWhere1: {
					id: this.id
				},
				caseWhere: {
					proposal: {
						policyId_contains: this.id
					}
				},
				proposalWhere: { policyId_contains: this.id }
			}

			this.isLoading = true
			const res = await api({ query, variables })
			this.isLoading = false
			if (res?.errors || res.data?.cases?.errors) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: res?.errors?.toString() || res.data?.cases?.errors
					}
				})
				return
			}
			console.log(res.data)
			this.caseId = res.data.cases.list[0].id
			this.proposalId = res.data.cases.list[0].proposals[0].id
		},

		async downloadPreviewFile() {
			let pdfDrawingQuery = gql`
				query renderPdfDrawingTemplate(
					$templateId: String!
					$input: templateRenderParametersInput
				) {
					renderPdfDrawingTemplate(templateId: $templateId, input: $input) {
						errors
						status
						value
					}
				}
			`

			let wkhtmltopdfQuery = gql`
				query renderWkhtmltopdfTemplate(
					$templateId: String!
					$input: templateRenderParametersInput
				) {
					renderWkhtmltopdfTemplate(templateId: $templateId, input: $input) {
						errors
						status
						value
					}
				}
			`

			let apiKey =
				this.selectedTemplate?.[0]?.typeName === "pdfDrawingTemplate"
					? "renderPdfDrawingTemplate"
					: "renderWkhtmltopdfTemplate"

			let query =
				this.selectedTemplate?.[0]?.typeName === "pdfDrawingTemplate"
					? pdfDrawingQuery
					: wkhtmltopdfQuery

			// HACK: remove 1ms from lastModifiedAt to get the state just before endorsement
			// const previousAsOf = new Date(
			// 	Date.parse(this.selectedEndorsement.lastModifiedAt) - 1
			// ).toISOString()

			// Create variables JSON string
			const variablesJsonString = JSON.stringify({
				policyWhere1: { id: this.id },
				endorsementWhere1: { id: this.selectedEndorsement.id },
				caseWhere1: { id: this.caseId }, // needed because in 'manage templates' we need the variables for the graphQlVariablesBuilder
				proposalWhere1: { id: this.proposalId } // needed because in 'manage templates' we need the variables for the graphQlVariablesBuilder
			})

			let variables = {
				templateId: this.selectedTemplate?.[0]?.value,
				input: {
					name: "data",
					variablesJsonString
				}
			}

			this.isLoading = true
			const res = await api({ query, variables })
			this.isLoading = false

			if (res.data?.[apiKey]?.errors && res.data?.[apiKey]?.errors?.length) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: res?.errors?.toString() || res.data?.[apiKey]?.errors
					}
				})
			}

			if (res.data?.[apiKey]?.status === "success") {
				let bytes = new Uint8Array(res.data?.[apiKey]?.value) // pass your byte response to this constructor
				let blob = new Blob([bytes], { type: "application/pdf" }) // change resultByte to bytes
				let link = document.createElement("a")
				link.href = window.URL.createObjectURL(blob)
				link.download = "test.pdf"
				link.click()
			}

			this.$modal.hide("downloadPdf")
		},

		async fetchTemplates(value = "") {
			const query = gql`
				query fetchTemplates {
					templates {
						totalCount
						list {
							id
							name
							createdAt
							lastModifiedAt
							__typename
							graphQlQuery
						}
					}
				}
			`

			let variables = {}
			this.isLoading = true
			const res = await api({ query, variables })
			this.isLoading = false

			this.templates = res?.data?.templates?.list
				?.filter(
					item =>
						item?.__typename === "pdfDrawingTemplate" ||
						item?.__typename === "wkhtmltopdfTemplate"
				)
				?.map(item => ({
					name: item.name,
					value: item.id,
					typeName: item.__typename,
					graphQlQuery: item.graphQlQuery
				}))
		},

		async createEndorsement() {
			if (!this.isEndorsable) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: "Issue the policy before creating an endorsement."
					}
				})
				return
			}
			const query = gql`
				mutation addEndorsement($policyId: String!) {
					addEndorsement(policyId: $policyId) {
						createdStatus {
							id
						}
						errors
						status
					}
				}
			`

			const {
				data: {
					addEndorsement: {
						status,
						createdStatus: { id: endorsementId },
						errors
					}
				}
			} = await api({
				query,
				variables: { policyId: this.id }
			})

			if (status === "success") {
				// If all good, redirect to EndorsementUpdate
				this.$router.push({
					name: "EndorsementsUpdate",
					params: { id: this.id, endorsementId: endorsementId }
				})
			} else if (errors) {
				this.$store.dispatch("showError", errors?.[0])
				return
			}

			// If errors, show message
		}
	},

	created() {
		this.getCaseIdProposalId()
		this.fetchEndorsements()
		this.fetchTemplates()
	}
}
</script>
